import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, Upload, Check, List, LayoutGrid, Grid3X3, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { companyAPI } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { storefrontColors, storefrontImageUrl, type StorefrontAppearance } from "../storefront-appearance";
import type { StorefrontSettingsModel } from "../hooks/useStorefrontSettings";
import { cn } from "@/lib/utils";
export function StorefrontAppearanceFields({ model, editing }: { model: StorefrontSettingsModel; editing: boolean }) {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [uploading, setUploading] = useState<string | null>(null);
	const a = model.draft.appearance;
	const customColor = !storefrontColors.some(color => color.toLowerCase() === a.accent.toLowerCase());
	const set = (key: keyof StorefrontAppearance, value: string) =>
		model.setDraft(previous => ({ ...previous, appearance: { ...previous.appearance, [key]: value } }));
	const upload = async (key: "logoUrl" | "coverUrl", file?: File) => {
		if (!file) return;
		if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type) || file.size > 8 * 1024 * 1024) {
			toast({
				title: t("common.error"),
				description: t("company.settings.storefront.banners.invalidImage"),
				variant: "destructive",
			});
			return;
		}
		setUploading(key);
		model.beginUpload();
		try {
			const result = await companyAPI.uploadStorefrontBanner(file);
			set(key, result.url);
		} catch {
			toast({
				title: t("common.error"),
				description: t("company.settings.storefront.banners.uploadFailed"),
				variant: "destructive",
			});
		} finally {
			setUploading(null);
			model.endUpload();
		}
	};
	return (
		<Card id="storefront-appearance">
			<CardHeader>
				<CardTitle>{t("storefrontDesign.appearance")}</CardTitle>
				<p className="text-sm text-muted-foreground">{t("storefrontDesign.identityHint")}</p>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="grid gap-3 sm:grid-cols-[120px_minmax(0,1fr)]">
					{(["logoUrl", "coverUrl"] as const).map(key => (
						<div key={key} className="min-w-0 space-y-2">
							<Label htmlFor={`storefront-${key}`}>
								{t(`storefrontDesign.${key === "logoUrl" ? "logo" : "cover"}`)}
							</Label>
							<div className="flex h-24 items-center justify-center overflow-hidden rounded-xl border border-dashed bg-muted/40">
								{a[key] ? (
									<img
										src={storefrontImageUrl(a[key])}
										alt={t(`storefrontDesign.${key === "logoUrl" ? "logo" : "cover"}`)}
										className={cn(
											"h-full w-full",
											key === "logoUrl" ? "object-contain p-2" : "object-cover",
										)}
									/>
								) : (
									<Image className="h-7 w-7 text-muted-foreground" aria-hidden />
								)}
							</div>
							{editing && (
								<div className="flex flex-wrap items-center gap-2">
									<Label className="relative inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-full border px-3 text-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring">
										<Upload className="h-4 w-4" aria-hidden />
										{t(uploading === key ? "common.loading" : "storefrontDesign.upload")}
										<input
											id={`storefront-${key}`}
											type="file"
											className="sr-only"
											accept="image/jpeg,image/png,image/webp,image/gif"
											disabled={!!uploading}
											onChange={e => {
												void upload(key, e.target.files?.[0]);
												e.target.value = "";
											}}
										/>
									</Label>
									{a[key] && (
										<Button variant="ghost" onClick={() => set(key, "")} disabled={!!uploading}>
											{t("company.settings.storefront.banners.remove")}
										</Button>
									)}
								</div>
							)}
						</div>
					))}
				</div>
				<div className="grid gap-3 sm:grid-cols-2">
					{(["displayName", "tagline"] as const).map(key => (
						<div key={key} className="space-y-2">
							<Label htmlFor={`storefront-${key}`}>{t(`storefrontDesign.${key}`)}</Label>
							<Input
								id={`storefront-${key}`}
								value={a[key]}
								maxLength={key === "displayName" ? 120 : 200}
								readOnly={!editing}
								placeholder={key === "displayName" ? model.profile?.name : model.profile?.description}
								onChange={e => set(key, e.target.value)}
							/>
						</div>
					))}
				</div>
				<fieldset className="space-y-2">
					<legend className="text-sm font-medium">{t("storefrontDesign.color")}</legend>
					<div className="flex flex-wrap gap-2">
						{storefrontColors.map((color, i) => (
							<button
								key={color}
								type="button"
								disabled={!editing}
								aria-label={t(`storefrontDesign.colors.${i}`)}
								aria-pressed={a.accent === color}
								onClick={() => set("accent", color)}
								className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-background text-white ring-1 ring-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-default"
								style={{ backgroundColor: color }}
							>
								{a.accent === color && <Check className="h-5 w-5" aria-hidden />}
							</button>
						))}
						<label
							title={t("storefrontDesign.customColor")}
							className={cn(
								"relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-background ring-1 ring-border has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2",
								editing && "cursor-pointer",
								customColor && "ring-2 ring-primary ring-offset-2",
							)}
							style={{
								background: customColor
									? a.accent
									: "conic-gradient(#ef4444, #eab308, #22c55e, #06b6d4, #6366f1, #d946ef, #ef4444)",
							}}
						>
							<span
								className="pointer-events-none grid h-6 w-6 place-items-center rounded-full bg-white text-slate-900"
								aria-hidden
							>
								<Palette className="h-4 w-4" />
							</span>
							<input
								id="storefront-custom-color"
								type="color"
								aria-label={t("storefrontDesign.customColor")}
								value={a.accent}
								disabled={!editing}
								onChange={e => set("accent", e.target.value)}
								className="absolute inset-0 h-full w-full cursor-inherit opacity-0"
							/>
						</label>
					</div>
				</fieldset>
				<fieldset className="space-y-2">
					<legend className="text-sm font-medium">{t("storefrontDesign.layout")}</legend>
					<div className="grid grid-cols-3 gap-2">
						{(["list", "grid2", "grid3"] as const).map((key, i) => {
							const Icon = [List, LayoutGrid, Grid3X3][i];
							return (
								<button
									type="button"
									key={key}
									disabled={!editing}
									aria-pressed={a.layout === key}
									onClick={() => set("layout", key)}
									className={cn(
										"flex min-h-16 flex-col items-center justify-center gap-1 rounded-xl border p-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
										a.layout === key
											? "border-primary bg-secondary text-primary"
											: "bg-background text-muted-foreground",
									)}
								>
									<Icon className="h-5 w-5" aria-hidden />
									{t(`storefrontDesign.layouts.${key}`)}
								</button>
							);
						})}
					</div>
				</fieldset>
				<div className="space-y-2">
					<Label htmlFor="storefront-announcement">{t("storefrontDesign.announcement")}</Label>
					<Input
						id="storefront-announcement"
						maxLength={240}
						value={a.announcement}
						readOnly={!editing}
						onChange={e => set("announcement", e.target.value)}
						placeholder={t("storefrontDesign.announcementHint")}
					/>
				</div>
			</CardContent>
		</Card>
	);
}
